/* eslint-disable react/no-danger */
import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { HtmlSanitizer } from 'roosterjs';
import classNames from 'classnames';

import { ReactTemplate } from '@bingads-webui-react/react-template';
import { Text, Link } from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Accordion } from '@bingads-webui-mca-react/step-manager';
import { useCurrentLocale } from '@bingads-webui-campaign-react/smart-page-context';

import { getPrivacyLink } from './privacy-content-configs';

const UnstyledPrivacyContent = ({
  value,
  businessName,
  i18n,
  classes,
}) => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const locale = useCurrentLocale();

  const policyHtmlContent = useMemo(
    () => HtmlSanitizer.sanitizeHtml(HtmlSanitizer.convertInlineCss(value)),
    [value]
  );

  const collapse = useCallback(() => {
    setExpandedIndex(-1);
  }, []);

  const expand = useCallback((index) => {
    setExpandedIndex(index);
  }, []);

  const privacyItemConfigs = useMemo(() => ([
    {
      name: 'custom-policy',
      title: i18n.getString(_TL_('{{businessName}} privacy policy'), { businessName }),
      summary: <Text block>{i18n.getString(_TL_('Learn more about how we handle your data.'))}</Text>,
      details: (
        <div className="custom-privacy-content">
          <div dangerouslySetInnerHTML={{ __html: policyHtmlContent }} />
        </div>
      ),
    },
    {
      name: 'ms-policy',
      isToggleHidden: true,
      isExpanded: true,
      title: i18n.getString(_TL_('Microsoft privacy policy ')),
      summary: <Text block>{i18n.getString(_TL_('Learn more about how Microsoft handles your data.'))}</Text>,
      details: (
        <div className="ms-privacy-content">
          <Text block>
            <ReactTemplate
              model={{
                link: text => (
                  <Link
                    className="ms-privacy-link"
                    href={getPrivacyLink(locale)}
                    target="_blank"
                  >
                    {text}
                  </Link>
                ),
              }}
              template={i18n.getString(_TL_('Smart Pages is built by Microsoft. Microsoft helps process any information you input. {{link}}Microsoft Privacy & Cookies{{/link}}'))}
            />
          </Text>
        </div>
      ),
    },
  ]), [i18n, policyHtmlContent, businessName, locale]);

  const accordionItems = useMemo(() => (
    privacyItemConfigs.map((config, index) => {
      const isExpanded = expandedIndex === index || config.isExpanded;
      const onToggle = isExpanded ? collapse : () => expand(index);

      return (
        <Accordion.Item
          key={config.name}
          isExpanded={isExpanded}
          onToggle={onToggle}
          isToggleHidden={config.isToggleHidden}
        >
          <Accordion.Header title={config.title} />
          <Accordion.Summary>
            {config.summary}
          </Accordion.Summary>
          <Accordion.Details>
            {config.details}
          </Accordion.Details>
        </Accordion.Item>
      );
    })
  ), [collapse, expand, expandedIndex, privacyItemConfigs]);

  return (
    <div className={classNames('privacy-content-root', classes.root)}>
      {accordionItems}
    </div>
  );
};

UnstyledPrivacyContent.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  businessName: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

export const PrivacyContent = withDefaultStyles(UnstyledPrivacyContent, () => ({
  root: {
    '& .ba-accordion-details': {
      minHeight: 20,
    },
    marginBottom: 30,
    overflowWrap: 'anywhere',
  },
}));
